// Embedding models a tenant has configured. Knowledge bases select one instead
// of carrying their own key.
//
// A caveat worth knowing while reading this: the backend refuses to change a
// connection's `model` while knowledge bases reference it, because their stored
// vectors were produced by the current one. The 409 message explains it, and
// this page surfaces that message verbatim.

import { ReturnBanner } from "@/components/return-banner";
import { useRouter } from "@/lib/navigation";
import { useReturnTo } from "@/lib/return-to";
import { ModelRegistry, type RegistryRow } from "@/components/models/model-registry";
import {
  createEmbeddingModel,
  deleteEmbeddingModel,
  testEmbeddingModel,
} from "@/lib/api/endpoints";
import { useEmbeddingModels, useModelCatalog } from "@/lib/query/hooks";
import { qk } from "@/lib/query/keys";

export default function EmbeddingModelsPage() {
  const returnTo = useReturnTo();
  const router = useRouter();

  const { data, isLoading, isError, error, refetch } = useEmbeddingModels();
  const catalog = useModelCatalog();

  return (
    <>
      {returnTo ? <ReturnBanner to={returnTo} noun="an embedding model" /> : null}
      <ModelRegistry
        onCreated={returnTo ? () => router.push(returnTo) : undefined}
        title="Embedding models"
        description="Models used to embed knowledge-base content. Configure one here and select it when creating a knowledge base."
        providers={["openai"]}
        // Embeddings are OpenAI-only, so the provider argument is ignored.
        catalogFor={() => catalog.data?.embedding ?? []}
        usedByNoun="knowledge bases"
        usedByCountNoun="knowledge bases"
        showDimensions
        rows={data as RegistryRow[] | undefined}
        isLoading={isLoading}
        isError={isError}
        error={error}
        refetch={() => void refetch()}
        queryKey={qk.embeddingModels}
        create={createEmbeddingModel}
        remove={deleteEmbeddingModel}
        test={testEmbeddingModel}
      />
    </>
  );
}
